'use client';

import { environment } from '@/config/environment';
import type {
  DemoAdminEventType,
  IDemoAdminAttachment,
  IDemoAdminSession,
  IDemoAdminShowcase,
} from '@/interfaces/demoAdmin';
import { demoBrowserContext, getDemoFingerprint } from '@/lib/demo-fingerprint';
import { getWebsiteSessionUuid } from '@/session-replay';

const SESSION_STORAGE_KEY = 'demo_admin_session_token';

type DemoAdminConversationKey = 'business' | 'table' | 'staff' | 'try';

function sessionStorageKey(conversationKey: DemoAdminConversationKey): string {
  // Keep the original key for the free-form demo so existing conversations
  // survive this migration. Guided conversations use their own agent session.
  return conversationKey === 'try'
    ? SESSION_STORAGE_KEY
    : `${SESSION_STORAGE_KEY}:${conversationKey}`;
}

class DemoAdminApiError extends Error {
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message);
  }
}

function baseUrl(): string {
  return environment.api.agentApiUrl.replace(/\/$/, '');
}

function headers(): HeadersInit {
  return {
    'Content-Type': 'application/json',
    'X-OB-Context': demoBrowserContext(getDemoFingerprint()),
  };
}

function languageCode(language: 'en' | 'zh'): 'en' | 'cn' {
  return language === 'zh' ? 'cn' : 'en';
}

export async function getDemoAdminShowcase(): Promise<IDemoAdminShowcase> {
  const response = await fetch(
    `${baseUrl()}/agent/public/demo/admin/showcase`,
    {
      method: 'GET',
      headers: headers(),
      cache: 'no-store',
    }
  );
  if (!response.ok) throw await errorFor(response);
  return (await response.json()) as IDemoAdminShowcase;
}

async function errorFor(response: Response): Promise<DemoAdminApiError> {
  const payload = (await response.json().catch(() => ({}))) as {
    message?: string;
  };
  return new DemoAdminApiError(
    payload.message || `The demo assistant returned HTTP ${response.status}.`,
    response.status
  );
}

async function requestSession(
  language: 'en' | 'zh',
  token?: string
): Promise<IDemoAdminSession> {
  const response = await fetch(`${baseUrl()}/agent/public/demo/admin/session`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({
      interaction: true,
      language: languageCode(language),
      ...(token ? { demo_admin_session_token: token } : {}),
    }),
  });
  if (!response.ok) throw await errorFor(response);
  return (await response.json()) as IDemoAdminSession;
}

export async function startDemoAdminSession(
  language: 'en' | 'zh',
  conversationKey: DemoAdminConversationKey = 'try'
): Promise<IDemoAdminSession> {
  const storageKey = sessionStorageKey(conversationKey);
  const stored = localStorage.getItem(storageKey) || undefined;
  try {
    const session = await requestSession(language, stored);
    localStorage.setItem(storageKey, session.demo_admin_session_token);
    return session;
  } catch (error) {
    if (!(error instanceof DemoAdminApiError) || error.status !== 401) {
      throw error;
    }
    localStorage.removeItem(storageKey);
    const session = await requestSession(language);
    localStorage.setItem(storageKey, session.demo_admin_session_token);
    return session;
  }
}

export async function restartDemoAdminSession(
  language: 'en' | 'zh',
  conversationKey: DemoAdminConversationKey = 'try'
): Promise<IDemoAdminSession> {
  const storageKey = sessionStorageKey(conversationKey);
  localStorage.removeItem(storageKey);
  const session = await requestSession(language);
  localStorage.setItem(storageKey, session.demo_admin_session_token);
  return session;
}

async function stream(
  path: string,
  body: Record<string, unknown>,
  onEvent: (event: DemoAdminEventType) => void
): Promise<void> {
  const response = await fetch(`${baseUrl()}${path}`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify(body),
  });
  if (!response.ok) throw await errorFor(response);
  if (!response.body) {
    throw new Error('The demo assistant returned an empty stream.');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  const consume = (block: string) => {
    const data = block
      .split(/\r?\n/)
      .filter(line => line.startsWith('data:'))
      .map(line => line.slice(5).trimStart())
      .join('\n');
    if (!data) return;
    try {
      onEvent(JSON.parse(data) as DemoAdminEventType);
    } catch {
      // A malformed event cannot safely be rendered, but later SSE events are
      // still useful and should continue to stream.
    }
  };

  while (true) {
    const { done, value } = await reader.read();
    buffer += decoder.decode(value, { stream: !done });
    const blocks = buffer.split(/\r?\n\r?\n/);
    buffer = blocks.pop() ?? '';
    blocks.forEach(consume);
    if (done) break;
  }
  if (buffer.trim()) consume(buffer);
}

export function streamDemoAdminMessage(
  session: IDemoAdminSession,
  language: 'en' | 'zh',
  message: string,
  attachments: IDemoAdminAttachment[],
  onEvent: (event: DemoAdminEventType) => void
): Promise<void> {
  return stream(
    '/agent/public/demo/admin/chat',
    {
      demo_admin_session_token: session.demo_admin_session_token,
      language: languageCode(language),
      message,
      ...(attachments.length ? { attachments } : {}),
      website_session_uuid: getWebsiteSessionUuid() ?? undefined,
    },
    onEvent
  );
}

export function confirmDemoAdminChange(
  session: IDemoAdminSession,
  language: 'en' | 'zh',
  requestId: string,
  approved: boolean,
  onEvent: (event: DemoAdminEventType) => void
): Promise<void> {
  return stream(
    `/agent/public/demo/admin/sessions/${encodeURIComponent(
      session.agent_session_uuid
    )}/confirm`,
    {
      demo_admin_session_token: session.demo_admin_session_token,
      language: languageCode(language),
      request_id: requestId,
      approved,
    },
    onEvent
  );
}

export async function respondToDemoAdminForm(
  session: IDemoAdminSession,
  language: 'en' | 'zh',
  requestId: string,
  data: Record<string, unknown>
): Promise<{ resolved: boolean; snapshot_cleared?: boolean }> {
  const response = await fetch(
    `${baseUrl()}/agent/public/demo/admin/sessions/${encodeURIComponent(
      session.agent_session_uuid
    )}/respond`,
    {
      method: 'POST',
      headers: headers(),
      body: JSON.stringify({
        demo_admin_session_token: session.demo_admin_session_token,
        language: languageCode(language),
        request_id: requestId,
        data,
      }),
    }
  );
  if (!response.ok) throw await errorFor(response);
  return (await response.json()) as {
    resolved: boolean;
    snapshot_cleared?: boolean;
  };
}
